import { createAction, props } from '@ngrx/store';
import {
  IUser,
  IListUserResponse,
  IListUsersRequest,
  IUserNamesResponse,
  IUserCreateRequest,
  IUserUpdateRequest,
  IUserListFilter,
  IUserListSort,
  IActiveSuperVisorsResponse,
  IgetUsersRequestPayload,
  IBulkResponse,
} from '../../models/user.model'; // Adjust path as necessary
import { WritableSignal } from '@angular/core';
import { IRole } from '../../models/role.model';

const createUser = createAction(
  '[User] Create User',
  props<{ user: IUserCreateRequest; step: number }>()
);

const createUserSuccess = createAction(
  '[User] Create User Success',
  props<{ user: IUser; step: number }>()
);

const createUserFailure = createAction(
  '[User] Create User Failure',
  props<{ error: string }>()
);

const listUsers = createAction('[User] List Users');

const listUsersSuccess = createAction(
  '[User] List Users Success',
  props<{ response: IListUserResponse; filters: IListUsersRequest }>()
);

const listUsersFailure = createAction(
  '[User] List Users Failure',
  props<{ error: string }>()
);

const listUserNames = createAction('[User] List User Names');

const listUserNamesSuccess = createAction(
  '[User] List User Names Success',
  props<{ response: IUserNamesResponse }>()
);

const listUserNamesFailure = createAction(
  '[User] List User Names Failure',
  props<{ error: string }>()
);

const listActiveSuperVisorsNames = createAction(
  '[User] List Active SuperVisors Names'
);

const listActiveSuperVisorsNamesSuccess = createAction(
  '[User] List Active SuperVisors Names Success',
  props<{ response: IActiveSuperVisorsResponse }>()
);

const listActiveSuperVisorsNamesFailure = createAction(
  '[User] List Active SuperVisors Names Failure',
  props<{ error: string }>()
);
const listAllSuperVisorsNames = createAction(
  '[User] List All SuperVisors Names',
  props<{ request: IgetUsersRequestPayload }>()
);

const listAllSuperVisorsNamesSuccess = createAction(
  '[User] List All SuperVisors Names Success',
  props<{ response: IActiveSuperVisorsResponse }>()
);

const listAllSuperVisorsNamesFailure = createAction(
  '[User] List All SuperVisors Names Failure',
  props<{ error: string }>()
);

const importUsers = createAction(
  '[User] Import Users',
  props<{
    file: File;
    clientIds: string;
    programIds: string;
    enableIpBasedAccess: boolean;
    stepSignal?: WritableSignal<number>;
  }>()
);
const importUsersSuccess = createAction(
  '[User] Import Users Success',
  props<{
    message: string;
    data: IBulkResponse['data'];
    stepSignal?: WritableSignal<number>;
  }>()
);
const importUsersFailure = createAction(
  '[User] Import Users Failure',
  props<{ error: string }>()
);

const updateUser = createAction(
  '[User] Update User',
  props<{
    id: number;
    user: IUserUpdateRequest;
    step: number; // Add step to track the current step in the form
  }>()
);

const updateUserSuccess = createAction(
  '[User] Update User Success',
  props<{ user: IUser; step: number }>()
);

const updateUserFailure = createAction(
  '[User] Update User Failure',
  props<{ error: string }>()
);

const deleteUser = createAction(
  '[User] Delete User',
  props<{ id: number; closeModalSignal: WritableSignal<boolean> }>()
);

const deleteUserSuccess = createAction(
  '[User] Delete User Success',
  props<{ id: number }>()
);

const deleteUserFailure = createAction(
  '[User] Delete User Failure',
  props<{ error: string }>()
);

const changeUserStatus = createAction(
  '[User] Change User Status',
  props<{ id: number }>()
);

const changeUserStatusSuccess = createAction(
  '[User] Change User Status Success'
);
const changeUserStatusFailure = createAction(
  '[User] Change User Status Failure',
  props<{ error: string }>()
);

const loadSingleUser = createAction(
  '[User] Load User',
  props<{ id: number; isUserPermission?: boolean }>()
);

const loadSingleUserSuccess = createAction(
  '[User] Load User Success',
  props<{ user: IUser | null }>()
);

const loadSingleUserFailure = createAction(
  '[User] Load User Failure',
  props<{ error: string }>()
);

const loadUserPermissionSuccess = createAction(
  '[User] Load UserPermissions Success',
  props<{ userPermission: IUser | null }>()
);
const clearUserPermissionSuccess = createAction(
  '[User] Clear UserPermissions Success'
);

const updateProfilePicture = createAction(
  '[User] User Profile Picture Uploaded Success',
  props<{ url: string; fileName: string }>()
);
const setUserQueryParams = createAction(
  '[User] Set User Query Params',
  props<{
    params: { page: number; limit: number };
    body?: { filter?: IUserListFilter; sort?: IUserListSort };
  }>()
);
const clearSingleUser = createAction('[User] Clear Single User');

const updateUserRoles = createAction(
  '[User] Update User Roles',
  props<{
    role: {
      role_id: string;
      permissions: IRole['permissions'];
      // Add step to track the current step in the form
    };
    id: number;
    step: number;
  }>()
);

const updateUserRolesSuccess = createAction(
  '[User] Update User Roles Success',
  props<{ user: IUser; step: number }>()
);

const updateUserRolesFailure = createAction(
  '[User] Update User Roles Failure',
  props<{ error: string }>()
);

const exportErrorsTemplate = createAction('[User] Export Errors Template');
const exportErrorsTemplatesuccess = createAction(
  '[User] Export Errors Template Success'
);
const exportErrorsTemplateFailure = createAction(
  '[User] Export Errors Template Failure',
  props<{ error: string }>()
);

const downloadUsersTemplate = createAction(
  '[User] Download Users Template',
  props<{
    client_ids: number[] | undefined;
    program_ids: number[] | undefined;
    status: boolean | undefined;
    role_id: number | undefined;
    supervisor_id: number | undefined;
    showNextSignal: WritableSignal<boolean>;
  }>()
);

const downloadUsersTemplateSuccess = createAction(
  '[User] Download Users Template Success'
);

const downloadUsersTemplateFailure = createAction(
  '[User] Download Users Template Failure',
  props<{ error: string }>()
);

const bulkUpdate = createAction(
  '[User] Bulk Update Users',
  props<{
    file: File;
    clientIds: string;
    programIds: string;
    enableIpBasedAccess: boolean;
    stepSignal?: WritableSignal<number>;
  }>()
);
const bulkUpdateSuccess = createAction(
  '[User] Bulk Update Users Success',
  props<{ message: string; data: IBulkResponse['data']; stepSignal?: WritableSignal<number> }>()
);
const bulkUpdateFailure = createAction(
  '[User] Bulk Update Users Failure',
  props<{ error: string }>()
);

export const UserActions = {
  createUser,
  createUserSuccess,
  createUserFailure,
  listUsers,
  listUsersSuccess,
  listUsersFailure,
  listUserNames,
  listUserNamesSuccess,
  listUserNamesFailure,
  listActiveSuperVisorsNames,
  listActiveSuperVisorsNamesSuccess,
  listActiveSuperVisorsNamesFailure,
  listAllSuperVisorsNames,
  listAllSuperVisorsNamesSuccess,
  listAllSuperVisorsNamesFailure,
  updateUser,
  updateUserSuccess,
  updateUserFailure,
  deleteUser,
  deleteUserSuccess,
  deleteUserFailure,
  changeUserStatus,
  changeUserStatusSuccess,
  changeUserStatusFailure,
  loadSingleUser,
  loadSingleUserFailure,
  loadSingleUserSuccess,
  // loadUserPermission,
  loadUserPermissionSuccess,
  // loadUserPermissionFailure,
  clearUserPermissionSuccess,
  setUserQueryParams,
  clearSingleUser,
  updateProfilePicture,
  updateUserRoles,
  updateUserRolesSuccess,
  updateUserRolesFailure,
  importUsers,
  importUsersSuccess,
  importUsersFailure,
  exportErrorsTemplate,
  exportErrorsTemplatesuccess,
  exportErrorsTemplateFailure,
  downloadUsersTemplate,
  downloadUsersTemplateSuccess,
  downloadUsersTemplateFailure,
  bulkUpdate,
  bulkUpdateSuccess,
  bulkUpdateFailure
};
